import { useState } from "react";
import { Link, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { AlertTriangle, Send, Trash2 } from "lucide-react";
import type { ChaveDeApi, DetalheDoSistema as Detalhe, Sistema } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useDefinirSituacaoDoSistema,
  useRevogarChave,
  useSistema,
  useTestarEntrega,
} from "@/api/consultas";
import { recursoInexistente, textoDoErro } from "@/api/problema";
import { AbasDaRota } from "@/componentes/AbasDaRota";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { BlocoDeCodigo } from "@/componentes/BlocoDeCodigo";
import { Barra } from "@/componentes/Barra";
import { Botao } from "@/componentes/Botao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { Interruptor } from "@/componentes/Interruptor";
import { NaoEncontrado } from "@/componentes/NaoEncontrado";
import { PaginaDeEntidade } from "@/componentes/PaginaDeEntidade";
import { PilulaDeExcecao } from "@/componentes/PilulaDeSituacao";
import { competencia, contagem, dataHora, documento, faz, quantidade } from "@/formato";
import { ROTA_DAS_APLICACOES, rotaDaAplicacao, rotaDasEntregas } from "@/rotas";
import { ConfigurarEntrega, RemoverSegredo } from "./ConfigurarEntrega";
import { urlSemSegredo, WEBHOOK_SEM_SEGREDO } from "./entrega";
import { CorrigirNome } from "./CorrigirNome";
import { EmitirChave } from "./EmitirChave";
import { AbaDeContrato } from "./AbaDeContrato";
import { ROTULO_DA_MODALIDADE } from "./cedente";
import { AbaDeAcesso } from "./AbaDeAcesso";
import { useAplicacaoDaRota } from "./useAplicacaoDaRota";

/*
  O algoritmo orienta a rotação (design D4 da add-protecao-contra-abuso): chave
  Argon2id ainda autentica, mas cada verificação fria dela custa 64 MiB, e é a
  rotação natural que a elimina. O painel recebe só o NOME — o hash não sai do
  banco —, e a tela não tenta deduzir mais nada dele.

  Só aparece o que pede ação: Argon2id ativa (rotacionar) e registro fora de
  formato (defeito de dado). O formato corrente não ganha rótulo nenhum — em toda
  chave nova ele seria ruído permanente na lista. Em chave revogada também some:
  ela não autentica mais, e não há rotação a orientar.
*/
const ALERTA_DO_ALGORITMO: Partial<
  Record<ChaveDeApi["algoritmo"], { rotulo: string; classe: string; dica: string }>
> = {
  argon2id: {
    rotulo: "argon2id: rotacionar",
    classe: "text-transito",
    dica: "Formato anterior, caro de verificar.",
  },
  desconhecido: {
    rotulo: "formato inválido",
    classe: "text-falha",
    dica: "Esta chave não autentica.",
  },
};

function AlertaDoAlgoritmo({
  algoritmo,
  revogada,
}: {
  algoritmo: ChaveDeApi["algoritmo"];
  revogada: boolean;
}) {
  const alerta = revogada ? undefined : ALERTA_DO_ALGORITMO[algoritmo];
  if (alerta === undefined) return null;

  return (
    <span className={`dados ${alerta.classe}`} title={alerta.dica}>
      {alerta.rotulo}
    </span>
  );
}

const ABAS = [
  { para: "contrato", rotulo: "Contrato" },
  { para: "cedentes", rotulo: "Cedentes" },
  { para: "chaves", rotulo: "Chaves de acesso" },
  { para: "acesso", rotulo: "Acesso" },
  { para: "entrega", rotulo: "Entrega" },
  { para: "consumo", rotulo: "Consumo" },
] as const;

const ROTULO_DO_MODO: Readonly<Record<string, string>> = { unitario: "Unitário", lote: "Lote" };

function avisoDaAba(aba: (typeof ABAS)[number]["para"], detalhe: Detalhe): string | undefined {
  if (aba === "acesso" && detalhe.acesso.aviso_sem_restricao) return "Sem restrição de origem";
  if (aba === "entrega" && urlSemSegredo(detalhe.sistema)) return WEBHOOK_SEM_SEGREDO;
  return undefined;
}

/*
 * Suspender e reativar, nas duas direções, são críticas: a suspensão derruba a integração do
 * cliente na chamada seguinte, e a reativação devolve a uma aplicação o acesso que alguém
 * tirou de propósito. Por isso não é `useAlternarSituacao`, que deixa o ligar leve.
 *
 * `destino` guarda o que foi pedido, e não a situação atual: a recusa de `409` relê o
 * detalhe, e o diálogo aberto não pode trocar de sentido por baixo do operador.
 *
 * `encerrado` não tem interruptor: a rota não sai dele.
 */
const TEXTO_DO_DESTINO = {
  suspenso: {
    verbo: "Suspender",
    corpo: "As chaves desta aplicação deixam de autenticar. O que já entrou segue o curso.",
    feito: "Aplicação suspensa",
  },
  ativo: {
    verbo: "Reativar",
    corpo: "As chaves desta aplicação voltam a autenticar. Nada é reenviado.",
    feito: "Aplicação reativada",
  },
} as const;

function AcoesDaAplicacao({ sistema }: { sistema: Sistema }) {
  const { cliente } = useSessao();
  const { feito } = useAvisos();
  const definir = useDefinirSituacaoDoSistema(cliente, sistema.id);

  const [destino, setDestino] = useState<keyof typeof TEXTO_DO_DESTINO | null>(null);
  const [corrigindo, setCorrigindo] = useState(false);

  const ativa = sistema.status === "ativo";
  const texto = destino === null ? null : TEXTO_DO_DESTINO[destino];

  return (
    <>
      <Botao tom="secundario" onClick={() => setCorrigindo(true)}>
        Corrigir nome
      </Botao>

      {(ativa || sistema.status === "suspenso") && (
        <Interruptor
          ligado={ativa}
          rotulo="Aplicação ativa"
          aoAlternar={() => setDestino(ativa ? "suspenso" : "ativo")}
        />
      )}

      <Confirmacao
        pedido={
          texto === null
            ? null
            : {
                severidade: "critica",
                titulo: `${texto.verbo} ${sistema.nome}?`,
                corpo: texto.corpo,
                verbo: `${texto.verbo} aplicação`,
                digitarParaLiberar: sistema.nome,
                exigeJustificativa: true,
              }
        }
        aoCancelar={() => setDestino(null)}
        aoConfirmar={async (motivo) => {
          if (destino === null) return;
          await definir.mutateAsync({ status: destino, motivo });
          setDestino(null);
          feito(TEXTO_DO_DESTINO[destino].feito);
        }}
      />

      {corrigindo && (
        <CorrigirNome
          sistemaID={sistema.id}
          nomeAtual={sistema.nome}
          aoFechar={() => setCorrigindo(false)}
        />
      )}
    </>
  );
}

/*
 * `/aplicacoes/:id`, a página da aplicação, no molde da página do cedente: seis abas com
 * formulário não cabem em painel de 520px (critério D1 da
 * `add-cedente-como-entidade-do-painel`), e ninguém compara aplicações linha a linha. Cada
 * aba é rota filha, com entrada no histórico, e a aba desconhecida não casa com rota
 * nenhuma: cai em "Endereço não encontrado".
 *
 * As abas só montam com o detalhe em mãos, e leem de `useAplicacaoDaRota`.
 */
export function DetalheDoSistema() {
  const { id = "" } = useParams();
  const { cliente, pode } = useSessao();
  const { data: detalhe, error } = useSistema(cliente, id);
  const sistema = detalhe?.sistema;

  if (recursoInexistente(error)) {
    return (
      <NaoEncontrado
        texto="Aplicação não encontrada."
        para={ROTA_DAS_APLICACOES}
        rotuloDoLink="Aplicações"
      />
    );
  }

  return (
    <PaginaDeEntidade
      key={id}
      secao="Aplicações"
      voltarPara={ROTA_DAS_APLICACOES}
      // Sem o nome, `Aplicação`: `undefined` diria que ainda está carregando, para sempre.
      titulo={sistema?.nome ?? (error !== null ? "Aplicação" : undefined)}
      selo={
        sistema !== undefined && (
          <PilulaDeExcecao
            normal={sistema.status === "ativo"}
            matiz={sistema.status === "suspenso" ? "transito" : "encerrado"}
          >
            {sistema.status}
          </PilulaDeExcecao>
        )
      }
      subtitulo={
        sistema !== undefined &&
        [
          documento(sistema.documento),
          `cobrança ${ROTULO_DA_MODALIDADE[sistema.cobranca.modalidade_cobranca]}`,
        ].join(" · ")
      }
      acoes={
        sistema !== undefined && pode("admin") ? <AcoesDaAplicacao sistema={sistema} /> : undefined
      }
    >
      {error !== null && <FaixaDeErro erro={error} />}

      {detalhe !== undefined ? (
        <AbasDaRota
          base={rotaDaAplicacao(id)}
          rotulo="Seções da aplicação"
          abas={ABAS.map((aba) => ({ ...aba, aviso: avisoDaAba(aba.para, detalhe) }))}
        >
          <Outlet context={detalhe} />
        </AbasDaRota>
      ) : (
        error === null && (
          <div aria-busy="true">
            <Esqueleto className="h-40" />
          </div>
        )
      )}
    </PaginaDeEntidade>
  );
}

/**
 * `/aplicacoes/:id`: abre em Contrato, que responde "esta aplicação emite hoje?".
 *
 * `replace` de propósito: o caminho sem aba não é um lugar, e o voltar não pode cair nele
 * para ser mandado de novo para a frente. O `state` segue junto, para o "« voltar" não
 * perder os filtros da lista.
 */
export function AbaPadraoDaAplicacao() {
  const { sistema } = useAplicacaoDaRota();
  const estado: unknown = useLocation().state;

  return <Navigate to={rotaDaAplicacao(sistema.id, "contrato")} replace state={estado} />;
}

export function ContratoDaAplicacao() {
  const { sistema } = useAplicacaoDaRota();

  return (
    <AbaDeContrato
      escopo={{ sistemaID: sistema.id }}
      nome={sistema.nome}
      plano={
        sistema.plano.vigente === true
          ? {
              limite_mensal: sistema.plano.limite_mensal ?? null,
              politica: sistema.plano.politica ?? "",
            }
          : null
      }
      ativa
    />
  );
}

export function ChavesDaAplicacao() {
  const { sistema, chaves } = useAplicacaoDaRota();
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();

  const [chaveARevogar, setChaveARevogar] = useState<ChaveDeApi | null>(null);

  const revogar = useRevogarChave(cliente, sistema.id);
  const ehAdmin = pode("admin");

  return (
    <div className="flex flex-col gap-3">
      {ehAdmin && <EmitirChave sistemaID={sistema.id} sistemaNome={sistema.nome} />}

      {chaves.length === 0 ? (
        <p className="text-texto-2">Nenhuma chave.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {chaves.map((chave) => {
            // A API escreve instante nulo como "", e não omite a chave.
            const revogada = Boolean(chave.revogada_em);

            return (
              <article
                key={chave.id}
                className={`rounded-controle border p-3 ${
                  revogada ? "border-fio bg-superficie-2/50 opacity-60" : "border-fio bg-superficie-2"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="dados truncate text-texto">{chave.prefixo}…</p>
                    <p className="mt-[2px] text-[12px] text-texto-2">
                      {chave.escopos.join(", ")}
                    </p>
                  </div>

                  {!revogada && ehAdmin && (
                    <Botao tom="destrutivo" onClick={() => setChaveARevogar(chave)}>
                      <Trash2 aria-hidden="true" className="size-4" />
                      Revogar
                    </Botao>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-texto-2">
                  {/*
                    O "último uso" é o que torna a rotação sem
                    indisponibilidade possível: ele mostra quando é
                    seguro revogar a antiga.
                  */}
                  <span title={chave.ultimo_uso_em}>
                    último uso:{" "}
                    <span className="dados">
                      {chave.ultimo_uso_em ? faz(chave.ultimo_uso_em) : "nunca usada"}
                    </span>
                  </span>
                  <AlertaDoAlgoritmo algoritmo={chave.algoritmo} revogada={revogada} />
                  {revogada && (
                    <span className="text-falha">revogada em {dataHora(chave.revogada_em)}</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Confirmacao
        pedido={
          chaveARevogar === null
            ? null
            : {
                severidade: "destrutiva",
                titulo: `Revogar a chave ${chaveARevogar.prefixo}?`,
                corpo: "A integração que usa esta chave para na hora.",
                verbo: "Revogar chave",
              }
        }
        aoCancelar={() => setChaveARevogar(null)}
        aoConfirmar={async () => {
          if (chaveARevogar === null) return;

          const resposta = await revogar.mutateAsync(chaveARevogar.id);
          setChaveARevogar(null);
          // O `202` diz que a propagação pode demorar o tempo do cache — e é
          // honesto de um jeito que `204` não seria.
          avisos.feito("Chave revogada", resposta?.aviso);
        }}
      />
    </div>
  );
}

export function AcessoDaAplicacao() {
  const { sistema, acesso } = useAplicacaoDaRota();

  return (
    <div className="flex flex-col gap-3">
      <AbaDeAcesso sistemaID={sistema.id} sistemaNome={sistema.nome} acesso={acesso} />
    </div>
  );
}

export function EntregaDaAplicacao() {
  const { sistema } = useAplicacaoDaRota();
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();

  const testar = useTestarEntrega(cliente, sistema.id);
  const [configurando, setConfigurando] = useState(false);

  const ehAdmin = pode("admin");
  const podeOperar = pode("operador");

  const entrega = sistema.entrega;
  // A API escreve a URL ausente como `""`: comparar com `undefined` ofereceria testar o nada.
  const url = entrega.url ?? "";
  const temWebhook = url !== "";
  const temSegredo = entrega.segredo_configurado === true;
  const semSegredo = urlSemSegredo(sistema);

  return (
    <div className="flex flex-col gap-3">
      {semSegredo && (
        <p className="flex items-center gap-2 rounded-controle border border-transito/40 bg-transito/10 px-3 py-2 text-corpo text-transito">
          <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
          {WEBHOOK_SEM_SEGREDO}
          <Dica texto="Sem segredo não há assinatura: cada evento novo é descartado, sem erro e sem retentativa. A referência entra em Trocar destino." />
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <CaixaDeCampo rotulo="Modo">{ROTULO_DO_MODO[entrega.modo ?? ""] ?? (entrega.modo || null)}</CaixaDeCampo>
        <CaixaDeCampo rotulo="Estado">{entrega.estado || null}</CaixaDeCampo>
        <CaixaDeCampo rotulo="Falhas consecutivas" dados>
          {entrega.falhas_consecutivas ?? 0}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Segredo de assinatura">
          {/*
            O segredo do HMAC nunca sai. O que a tela precisa saber é se
            ele existe — sem ele não há como assinar, e o teste falharia
            por isso, não por rede.
          */}
          {temSegredo ? (
            "configurado"
          ) : (
            <span className={semSegredo ? "text-transito" : undefined}>não configurado</span>
          )}
        </CaixaDeCampo>
      </div>

      {temWebhook ? (
        <BlocoDeCodigo rotulo="Destino" conteudo={url} />
      ) : (
        <p className="text-texto-2">Sem webhook.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/*
          Configurar exige `admin`, e testar exige `operador`: são poderes
          diferentes de propósito. Testar dispara um evento sintético contra
          um endereço que alguém já aprovou; configurar ESCOLHE o endereço.
        */}
        {ehAdmin && (
          <Botao tom="secundario" onClick={() => setConfigurando(true)}>
            {temWebhook ? "Trocar destino" : "Configurar entrega"}
          </Botao>
        )}

        {/* A rota exige `webhook_url`, e sem URL não há o que mandar junto do gesto. */}
        {ehAdmin && temWebhook && temSegredo && (
          <RemoverSegredo sistemaID={sistema.id} sistemaNome={sistema.nome} url={url} />
        )}

        {podeOperar && temWebhook && (
          <>
            <Botao
              tom="secundario"
              carregando={testar.isPending}
              onClick={() => {
                testar.mutate(undefined, {
                  onSuccess: (diagnostico) => {
                    const ok =
                      diagnostico.status !== undefined &&
                      diagnostico.status >= 200 &&
                      diagnostico.status < 300;
                    const resumo = diagnostico.erro
                      ? diagnostico.erro
                      : `HTTP ${diagnostico.status} em ${diagnostico.latencia_ms} ms`;

                    // O status de erro do cliente também vem em 200: a
                    // requisição de teste funcionou, o que falhou foi o
                    // destino. Dizer "falhou" nos dois casos esconderia a
                    // diferença que importa.
                    if (ok) avisos.feito("Entrega de teste aceita", resumo);
                    else avisos.falhou(`O destino respondeu: ${resumo}`);
                  },
                  onError: (falha) => avisos.falhou(textoDoErro(falha)),
                });
              }}
            >
              <Send aria-hidden="true" className="size-4" />
              Testar entrega
            </Botao>
            <Dica texto="Evento sintético teste.entrega, com a mesma assinatura das entregas reais. Nada vai para o histórico e não há retentativa." />
          </>
        )}

        <Link
          to={rotaDasEntregas({ sistema_id: sistema.id })}
          className="ml-auto text-[12px] text-texto-2 underline-offset-2 hover:text-texto hover:underline"
        >
          Histórico de entregas
        </Link>
      </div>

      {configurando && (
        <ConfigurarEntrega
          sistemaID={sistema.id}
          sistemaNome={sistema.nome}
          urlAtual={url}
          segredoConfigurado={temSegredo}
          degradado={entrega.estado === "degradado"}
          aoFechar={() => setConfigurando(false)}
        />
      )}

      {testar.data !== undefined && (
        <div className="flex flex-col gap-3">
          <BlocoDeCodigo rotulo="Cabeçalhos enviados" conteudo={testar.data.cabecalhos} />
          <BlocoDeCodigo rotulo="Corpo enviado" conteudo={testar.data.corpo_enviado} />
          <BlocoDeCodigo
            rotulo={`Resposta do destino${testar.data.truncado === true ? " (truncada)" : ""}`}
            conteudo={testar.data.corpo_recebido}
            vazio="O destino respondeu sem corpo."
          />
        </div>
      )}
    </div>
  );
}

export function ConsumoDaAplicacao() {
  const { sistema, consumo } = useAplicacaoDaRota();

  return (
    <div className="flex flex-col gap-3">
      <p className="rotulo">competência {competencia(consumo.competencia)}</p>

      {consumo.tetos.length === 0 ? (
        <p className="text-texto-2">Nenhum teto medido.</p>
      ) : (
        consumo.tetos.map((teto, indice) => {
          // Limite nulo no plano tem DOIS sentidos: plano sem limite, ou
          // aplicação sem contrato vigente — que não emite nada. Ler o
          // segundo como "sem limite" prometeria capacidade infinita a
          // quem tem capacidade zero; por isso o servidor marca
          // `sem_contrato`, e a tela confere também o cabeçalho, como a
          // aba Contrato.
          const semContrato =
            teto.nivel === "plano" &&
            (teto.sem_contrato === true || sistema.plano?.vigente !== true);
          return (
            <div key={`${teto.nivel}-${teto.pessoa_id ?? indice}`}>
              <div className="flex items-baseline justify-between text-[12px]">
                <span className="text-texto">
                  {teto.nivel === "plano" ? "Plano" : teto.pessoa_nome || "Vínculo"}
                  {!semContrato && <span className="rotulo ml-2">{teto.politica}</span>}
                </span>
                <span className={semContrato ? "dados text-falha" : "dados text-texto-2"}>
                  {semContrato
                    ? `${quantidade(teto.emitidos)} · sem contrato`
                    : teto.limite_mensal === null
                      ? `${quantidade(teto.emitidos)} · sem limite`
                      : `${quantidade(teto.emitidos)}/${quantidade(teto.limite_mensal)}`}
                </span>
              </div>

              {/*
                Plano SEM LIMITE não tem barra: não há proporção de nada.
                Uma barra sobre limite zero diria que a franquia acabou —
                o oposto do que o plano é.
              */}
              {!semContrato && teto.limite_mensal !== null && (
                <div className="mt-1">
                  <Barra
                    usado={teto.emitidos}
                    total={teto.limite_mensal}
                    rotulo={`Consumo do nível ${teto.nivel}`}
                  />
                </div>
              )}

              {teto.excedentes > 0 && (
                <p className="mt-1 text-[12px] text-transito">
                  {contagem(teto.excedentes, "excedente", "excedentes")}
                  {/*
                    Teto de excedente AUSENTE significa ilimitado, e não
                    zero: `overage` sem teto não tem limite, e mostrar 0
                    diria o oposto.
                  */}
                  {teto.teto_excedente === undefined
                    ? " · sem teto de excedente"
                    : ` de ${teto.teto_excedente}`}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
